import React, { useMemo } from "react";

interface ResponsiveFlexGridProps {
  items: any[];
  maxColumns?: {
    xs?: number; // default: 2
    sm?: number; // default: 3
    md?: number; // default: 4
    lg?: number; // default: 5
  };
  gap?: {
    default?: string; // default: "1.5rem" (gap-6)
    lg?: string; // default: "1.25vw"
  };
  className?: string;
  containerClassName?: string;
  renderItem: (item: any, index: number) => React.ReactNode;
  cardClassName?: string;
}

const ResponsiveFlexGrid: React.FC<ResponsiveFlexGridProps> = ({
  items,
  maxColumns = {
    xs: 2,
    sm: 3,
    md: 4,
    lg: 5,
  },
  gap = {
    default: "1.5rem",
    lg: "1.25vw",
  },
  className = "",
  containerClassName = "",
  renderItem,
  cardClassName = "",
}) => {
  const { xs = 2, sm = 3, md = 4, lg = 5 } = maxColumns;
  const { default: defaultGap = "1.5rem", lg: lgGap = "1.25vw" } = gap;

  // Generate unique ID for this instance to avoid CSS conflicts
  const instanceId = useMemo(() => `flex-grid-${Math.random().toString(36).substr(2, 9)}`, []);

  // Calculate remaining items for each breakpoint
  const totalItems = items.length;
  const lgRemaining = totalItems % lg;
  const mdRemaining = totalItems % md;
  const smRemaining = totalItems % sm;
  const xsRemaining = totalItems % xs;

  // Generate CSS styles
  const styles = useMemo(() => {
    let css = `
      .${instanceId}-card {
        width: calc((100% - ${xs - 1} * ${defaultGap}) / ${xs}); /* xs: ${xs} columns */
      }
      @media (min-width: 640px) {
        .${instanceId}-card {
          width: calc((100% - ${sm - 1} * ${defaultGap}) / ${sm}); /* sm: ${sm} columns */
        }
      }
      @media (min-width: 768px) {
        .${instanceId}-card {
          width: calc((100% - ${md - 1} * ${defaultGap}) / ${md}); /* md: ${md} columns */
        }
      }
      @media (min-width: 1024px) {
        .${instanceId}-card {
          width: calc((100% - ${lg - 1} * var(--lg-gap, ${lgGap})) / ${lg}); /* lg: ${lg} columns */
        }
      }
    `;

    // Add centering styles for remaining items
    const needsCenteringXs = xsRemaining > 0 && xsRemaining < xs;
    const needsCenteringSm = smRemaining > 0 && smRemaining < sm;
    const needsCenteringMd = mdRemaining > 0 && mdRemaining < md;
    const needsCenteringLg = lgRemaining > 0 && lgRemaining < lg;

    if (needsCenteringXs) {
      css += `.${instanceId}-container-xs { justify-content: center; }`;
    }
    if (needsCenteringSm) {
      css += `@media (min-width: 640px) { .${instanceId}-container-sm { justify-content: center; } }`;
    }
    if (needsCenteringMd) {
      css += `@media (min-width: 768px) { .${instanceId}-container-md { justify-content: center; } }`;
    }
    if (needsCenteringLg) {
      css += `@media (min-width: 1024px) { .${instanceId}-container-lg { justify-content: center; } }`;
    }

    return css;
  }, [instanceId, xs, sm, md, lg, defaultGap, lgGap, xsRemaining, smRemaining, mdRemaining, lgRemaining]);

  // Generate container classes
  const containerClasses = useMemo(() => {
    const classes = [instanceId + "-container"];
    
    if (xsRemaining > 0 && xsRemaining < xs) classes.push(`${instanceId}-container-xs`);
    if (smRemaining > 0 && smRemaining < sm) classes.push(`${instanceId}-container-sm`);
    if (mdRemaining > 0 && mdRemaining < md) classes.push(`${instanceId}-container-md`);
    if (lgRemaining > 0 && lgRemaining < lg) classes.push(`${instanceId}-container-lg`);
    
    return classes.join(" ");
  }, [instanceId, xsRemaining, smRemaining, mdRemaining, lgRemaining, xs, sm, md, lg]);

  // Use Tailwind gap class if it matches common values, otherwise use inline style
  const getGapClass = () => {
    if (defaultGap === "1.5rem" || defaultGap === "24px") return "gap-6";
    return "";
  };

  const containerStyle: React.CSSProperties = 
    getGapClass() ? {} : { gap: defaultGap };

  return (
    <div className={className}>
      <style>{styles}</style>
      <div
        className={`flex flex-wrap ${getGapClass()} ${lgGap === "1.25vw" ? "lg:gap-[1.25vw]" : ""} ${containerClasses} ${containerClassName}`}
        style={containerStyle}
      >
        {items.map((item, index) => (
          <div
            key={item?.id || index}
            className={`${instanceId}-card ${cardClassName}`}
          >
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResponsiveFlexGrid;
